import { Injectable } from "@angular/core";
import { GridLayoutService } from "@irb-common/interfaces/grid-layout.service";
import { DeleteCellComponent } from "@irb-common/components/grid/renderers/delete.cell.component";
import { DeleteHeaderComponent } from "@irb-common/components/grid/renderers/delete.header.component";
import { RangeHeaderComponent } from "@irb-common/components/grid/renderers/range.header.component";
import { RangeCellComponent } from "@irb-common/components/grid/renderers/range.cell.component";
import { CallToActionCellComponent } from "@irb-common/components/grid/renderers/calltoaction.cell.component";
import { TruncateTooltipCellComponent } from '@irb-common/components/grid/renderers/truncatetooltip.cell.component';
import { ComPrCashValuesComponent } from "@irb/irb-editors/editor-portfolio-result/com-pr-cash-values/com-pr-cash-values.component";
import { UtilityService } from "@irb-common/services/utility.service";
import {ICellRendererParams} from "ag-grid-community";
import { DecimalPipe } from '@angular/common';

@Injectable({
    providedIn: "root"
})
export class ComPrCashValuesGridLayoutService implements GridLayoutService<ComPrCashValuesComponent> {

    constructor(private util: UtilityService, private decimalPipe: DecimalPipe) {
    }

    getFrameworkComponents() {
        return {
            "CallToActionCellComponent": CallToActionCellComponent,
            "RangeHeaderComponent": RangeHeaderComponent,
            "RangeCellComponent": RangeCellComponent,
            "DeleteHeaderComponent": DeleteHeaderComponent,
            "DeleteCellComponent": DeleteCellComponent,
            "TruncateTooltipCellComponent": TruncateTooltipCellComponent,
        };
    }

    getColumnDefs(parent: ComPrCashValuesComponent) {
        return [{
            headerName: "Account",
            marryChildren:true,
            suppressMovable: true,
            children: [
                {
                    headerName: "Acct#",
                    suppressMovable: true,
                    field: "accountNum",
                    flex: 1,
                    sort: "asc",
                    comparator: this.util.sortString.bind(parent),
                    cellClass: "no-link-text",
                    cellRendererSelector:(params: any) => this.accountRenderer(params, parent),
                    cellClassRules: {
                        'add-account-liquidated-mark': (params: any) => {
                            if (params.data && params.data.liquidated) {
                                return true;
                            }
                            return false;
                        },
                        'footer': (params: any) => {
                            if (params.node && params.node.rowPinned) {
                                return true;
                            }
                            return false;
                        },
                    },
                },
                {
                    headerName: "Market Value",
                    suppressMovable: true,
                    field: "mktVal",
                    flex: 1,
                    cellRenderer : 'cellRendererFormatter',
                    cellRendererParams: (params: ICellRendererParams) => (
                      {type: 'currency', decimalPosition: '2', isBlank: params.node.rowPinned}),
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                }
            ]
        },
        {
            headerGroupComponent: "RangeHeaderComponent",
            headerGroupComponentParams: { 
                title: "Accounts with cash in range:",
                count: () => parent.getInRangeCount("isWithinRangeBefore")
            },
            headerClass: () => (parent.isAllInRange("isWithinRangeBefore")) ? "in-range" : "out-of-range",
            marryChildren:true,
            suppressMovable: true,
            children: [
                {
                    headerName: "Total",
                    suppressMovable: true,
                    field: "totalCash",
                    flex: 1,
                    cellRenderer : 'cellRendererFormatter',
                    cellRendererParams: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'},
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                },
                {
                    headerName: "Unavailable",
                    headerComponentParams: {
                        irbColInfo: this.unavailCashInfo(parent)
                    },
                    suppressMovable: true,
                    field: "unavailCash",
                    cellRendererSelector:(params: any) => this.unavailableCashRenderer(params, parent),
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : 'no-link-text irb-cell-content-right',
					flex: 1                },
                {
                    headerName: "Available",
                    suppressMovable: true,
                    field: "availCash",
                    flex: 1,
                    cellRenderer : 'cellRendererFormatter',
                    cellRendererParams: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'},
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                },                
                ...(parent.isCashEqvEnabled ? [
                    {
                        headerName: "Equivalents",
                        suppressMovable: true,
                        field: "cashEquivAvail",
                        flex: 1,
                        cellRenderer : 'cellRendererFormatter',
                        cellRendererParams: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'},
                        cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                    },
                    {
                        headerName: "Cash & Equivalents",
                        suppressMovable: true,
                        colId: "cashAndEquivalentsBefore",
                        valueGetter: (params: any) => {
                            if (!params.data) return null;
                            const avail = params.data.availCash ?? 0;
                            const equiv = params.data.cashEquivAvail ?? 0;
                            return avail + equiv;
                        },
                        flex: 1,
                        cellRenderer : 'cellRendererFormatter',
                        cellRendererParams: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'},
                        cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                    }
                ] : []),
                {
                    headerName: "",
                    suppressMovable: true,
                    cellRenderer : 'RangeCellComponent',
                    cellRendererParams: (params: any) => { 
                        return {
                            "inRange": params.data.isWithinRangeBefore,
                            "aboveRange": params.data.isAboveRangeBefore,
                            "excluded": params.node.rowPinned
                        };
                    },
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                    minWidth: 25,
                    maxWidth: 25,
                    width: 25
                }
            ]
        },
        {
            headerName: "Adjustments",
            headerClass: "adjustment",
            headerGroupComponent: 'customHeaderGroup',
            headerGroupComponentParams: {
                irbColInfo: this.getAdjustmentsColInfo()
            },
            marryChildren:true,
            suppressMovable: true,
            children: [
                {
                    headerName: "Cash Adjustments",
                    suppressMovable: true,
                    headerClass: "adjustment",
                    colId: "cashAdj",
                    flex: 1,
                    valueGetter: (params: any) => {
                        if (!params.data) return null;
                        const cashAdj = params.data.cashAdj ?? 0;
                        const equiv = params.data.cashEquivAdj ?? 0;
                        return cashAdj + (parent.isCashEqvEnabled ? equiv : 0);
                    },
                    cellRenderer : 'cellRendererFormatter',
                    cellRendererParams: {type: 'currency', decimalPosition: '2'},
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : 'adjustment',
                }
            ]
        },
        {
            headerGroupComponent: "RangeHeaderComponent",
            headerGroupComponentParams: { 
                title: "Accounts with cash in range:",
                count: () => parent.getInRangeCount("isWithinRangeAfter")
            },
            headerClass: () => (parent.isAllInRange("isWithinRangeAfter")) ? "in-range" : "out-of-range",
            marryChildren:true,
            suppressMovable: true,
            children: [
                {
                    headerName: "Total",
                    suppressMovable: true,
                    field: "afterRebalTotalCash",
                    flex: 1,
                    cellRenderer : 'cellRendererFormatter',
                    cellRendererParams: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'},
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                },
                {
                    headerName: "Unavailable",
                    headerComponentParams: {
                        irbColInfo: this.unavailCashInfo(parent)
                    },
                    suppressMovable: true,
                    field: "unavailCash",
                    cellRendererSelector:(params: any) => this.unavailableCashRenderer(params, parent),
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : 'no-link-text irb-cell-content-right',
					flex: 1
                },
                {
                    headerName: "Available",
                    suppressMovable: true,
                    field: "afterRebalAvailAmt",
                    flex: 1,
                    cellRenderer : 'cellRendererFormatter',
                    cellRendererParams: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'},
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                },
                ...(parent.isCashEqvEnabled ? [
                  {
                      headerName: "Equivalents",
                      suppressMovable: true,
                      field: "cashEquivAfterAdj",
                      flex: 1,
                      cellRenderer : 'cellRendererFormatter',
                      cellRendererParams: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'},
                      cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                  },
                  {
                      headerName: "Cash & Equivalents",
                      suppressMovable: true,
                      colId: "cashAndEquivalentsAfter",
                      valueGetter: (params: any) => {
                          if (!params.data) return null;
                          const avail = params.data.afterRebalAvailAmt ?? 0;
                          const equiv = params.data.cashEquivAfterAdj ?? 0;
                          return avail + equiv;
                      },
                      flex: 1,
                      cellRenderer : 'cellRendererFormatter',
                      cellRendererParams: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'},
                      cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                  }
                ] : []),
                {
                    headerName: "",
                    suppressMovable: true,
                    cellRenderer : 'RangeCellComponent',
                    cellRendererParams: (params: any) => { 
                        return {
                            "inRange": params.data.isWithinRangeAfter,
                            "aboveRange": params.data.isAboveRangeAfter,
                            "excluded": params.node.rowPinned
                        };
                    },
                    cellClass: (params: ICellRendererParams) => params.node.rowPinned ? 'footer' : '',
                    minWidth: 25,
                    maxWidth: 25,
                    width: 25
                }
            ]
        }
    ];
    }

    getAdjustmentsColInfo() {
        return `
          This is the estimated amount that cash would be adjusted if the trades are approved and executed - in other words, the net of buys and sells in this account. To change the cash adjustment, you can edit the trades for this account in the Proposed Orders section below.
          `
    }

    unavailCashInfo(parent: ComPrCashValuesComponent) {
        let s = "Unavailable cash is cash that has been reserved and is excluded from the account when rebalancing to target.";
        
        if(parent.isDistHoldCashEnabled) {
            s += " Unavailable cash is the greater of Set-aside Cash and Distribution Cash.";
        }

        return s;
    }

    accountRenderer(params: any, parent: any) {
        if (params && params.node && params.node.rowPinned) {
            return {
                component: 'cellRendererFormatter',
                params: {value: params.value}
            }
        } else {
            return {
                component: 'CallToActionCellComponent',
                params:{
                    getTemplate: (params: any) => parent.getTemplate(params),
                    calltoactionUsesHover: true,
                    calltoactionMaxWidth: 'none',
                    calltoactionHorizontalOffset: -80
                }
            }
        }
    }

    unavailableCashRenderer(params: any, parent: any) {
        if (params && params.node && params.node.rowPinned) {
            return {
                component: 'cellRendererFormatter',
                params: {type: 'currency', displaySymbol: ' ', decimalPosition: '2'}
            }
        } else {
            return {
                component: 'CallToActionCellComponent',
                params:{
                    getTemplate: (params: any) => parent.getTemplate(params),
                    calltoactionUsesHover: true,
                    calltoactionMaxWidth: 'none',
                    calltoactionHorizontalOffset: 10,
                    valueFormatter: (params: any) => {
                        return (params.value !== 0) ? this.decimalPipe.transform(params.value, '1.2-2') : params.value;
                    }
                }
            }
        }
    }
}
